import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { Calendar as CalendarIcon, ArrowLeft, PartyPopper, Check, Loader2 } from 'lucide-react';
import { format } from 'date-fns';

import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { useLanguage } from '@/context/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Animation variants
const fadeInLeft = { initial: { opacity: 0, x: -20 }, animate: { opacity: 1, x: 0 } };
const fadeInRight = { initial: { opacity: 0, x: 20 }, animate: { opacity: 1, x: 0 } };
const fadeInUp = { initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 } };

const BirthdayBookingPage = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [date, setDate] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    mobile_phone: '',
    number_of_guests: ''
  });

  const fetchPackages = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/packages`);
      setPackages(response.data);
    } catch (err) {
      toast.error('Failed to load packages', {
        description: 'Please refresh the page to try again'
      });
    }
  }, []);

  useEffect(() => {
    fetchPackages();
  }, [fetchPackages]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!date || !selectedPackage) {
      toast.error(t('birthdayBooking.selectDateAndPackage'));
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/bookings/birthday`, {
        ...formData,
        date: format(date, 'yyyy-MM-dd'),
        number_of_guests: parseInt(formData.number_of_guests),
        package_type: selectedPackage
      });
      
      toast.success('Birthday party booked successfully!', {
        description: 'We will contact you shortly to confirm your booking.'
      });
      
      // Reset form
      setFormData({ name: '', mobile_phone: '', number_of_guests: '' });
      setDate(null);
      setSelectedPackage(null);
      
    } catch (error) {
      toast.error(t('birthdayBooking.errorTitle'), {
        description: error.response?.data?.detail || t('birthdayBooking.errorDescription')
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-brand-cream">
      <Header />
      
      <main className="pt-28 pb-20">
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          {/* Back Button */}
          <motion.button
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-gray-600 hover:text-brand-orange transition-colors mb-8 font-body"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('birthdayBooking.backToHome')}
          </motion.button>

          {/* Page Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-brand-orange/10 mb-4">
              <PartyPopper className="w-8 h-8 text-brand-orange" />
            </div>
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              {t('birthdayBooking.title')}
            </h1>
            <p className="font-body text-lg text-gray-600 max-w-2xl mx-auto">
              {t('birthdayBooking.subtitle')}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Package Selection */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
              className="space-y-6"
            >
              <h2 className="font-heading text-2xl font-bold text-gray-900">
                {t('birthdayBooking.choosePackage')}
              </h2>
              
              <div className="space-y-4">
                {packages.map((pkg) => (
                  <div
                    key={pkg.id}
                    data-testid={`package-${pkg.id}`}
                    onClick={() => setSelectedPackage(pkg.id)}
                    className={cn(
                      "relative p-6 rounded-2xl border-2 cursor-pointer transition-all",
                      selectedPackage === pkg.id
                        ? "border-brand-orange bg-brand-orange/5 shadow-lg"
                        : "border-gray-200 bg-white hover:border-brand-orange/50 hover:shadow-md"
                    )}
                  >
                    {selectedPackage === pkg.id && (
                      <div className="absolute top-4 right-4 w-6 h-6 rounded-full bg-brand-orange flex items-center justify-center">
                        <Check className="w-4 h-4 text-white" />
                      </div>
                    )}
                    
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h3 className="font-heading font-bold text-xl text-gray-900">{pkg.name}</h3>
                        <p className="font-body text-sm text-gray-500">{pkg.description}</p>
                      </div>
                      <div className="text-right">
                        <span className="font-heading text-xl font-bold text-brand-orange">{pkg.price}</span>
                      </div>
                    </div>
                    
                    <ul className="space-y-2">
                      {pkg.features?.map((feature, idx) => (
                        <li key={idx} className="flex items-center gap-2 font-body text-sm text-gray-600">
                          <Check className="w-4 h-4 text-brand-orange" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Booking Form */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
            >
              <div className="bg-white p-8 rounded-3xl shadow-xl border border-gray-100">
                <h2 className="font-heading text-2xl font-bold text-gray-900 mb-6">
                  {t('birthdayBooking.yourDetails')}
                </h2>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Name */}
                  <div className="space-y-2">
                    <Label htmlFor="name" className="font-heading font-bold text-gray-700">
                      {t('birthdayBooking.fullName')}
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      data-testid="birthday-name-input"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder={t('birthdayBooking.fullNamePlaceholder')}
                      required
                      className="h-12 rounded-xl border-gray-200 focus:border-brand-orange focus:ring-brand-orange/20"
                    />
                  </div>

                  {/* Phone */}
                  <div className="space-y-2">
                    <Label htmlFor="mobile_phone" className="font-heading font-bold text-gray-700">
                      {t('birthdayBooking.mobilePhone')}
                    </Label>
                    <Input
                      id="mobile_phone"
                      name="mobile_phone"
                      data-testid="birthday-phone-input"
                      value={formData.mobile_phone}
                      onChange={handleInputChange}
                      placeholder="+1 (234) 567-890"
                      required
                      className="h-12 rounded-xl border-gray-200 focus:border-brand-orange focus:ring-brand-orange/20"
                    />
                  </div>

                  {/* Date Picker */}
                  <div className="space-y-2">
                    <Label className="font-heading font-bold text-gray-700">
                      {t('birthdayBooking.partyDate')}
                    </Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          data-testid="birthday-date-picker"
                          className={cn(
                            "w-full h-12 justify-start text-left font-normal rounded-xl border-gray-200",
                            !date && "text-muted-foreground"
                          )}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {date ? format(date, "PPP") : t('birthdayBooking.selectDate')}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={date}
                          onSelect={setDate}
                          disabled={(date) => date < new Date()}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>

                  {/* Number of Guests */}
                  <div className="space-y-2">
                    <Label htmlFor="number_of_guests" className="font-heading font-bold text-gray-700">
                      {t('birthdayBooking.numberOfGuests')}
                    </Label>
                    <Input
                      id="number_of_guests"
                      name="number_of_guests"
                      type="number"
                      data-testid="birthday-guests-input"
                      value={formData.number_of_guests}
                      onChange={handleInputChange}
                      placeholder="10"
                      min="1"
                      max="50"
                      required
                      className="h-12 rounded-xl border-gray-200 focus:border-brand-orange focus:ring-brand-orange/20"
                    />
                  </div>

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    data-testid="birthday-submit-btn"
                    disabled={loading || !selectedPackage}
                    className="w-full h-14 bg-brand-orange hover:bg-orange-600 text-white rounded-full font-heading font-bold text-lg shadow-lg hover:shadow-xl transition-all hover:scale-105 disabled:opacity-50 disabled:hover:scale-100"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        {t('birthdayBooking.booking')}
                      </>
                    ) : (
                      <>
                        <PartyPopper className="w-5 h-5 mr-2" />
                        {t('birthdayBooking.bookBirthdayParty')}
                      </>
                    )}
                  </Button>
                </form>
              </div>
            </motion.div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default BirthdayBookingPage;
